/**
 * FBCLID Service - Handles capturing, storing, and retrieving fbclid parameter
 */

import { getStoredConsent, isMarketingAllowed } from "./cookieConsentService";
import { getStoredRegionInfo } from "./regionService";
import { getCurrentUserId } from "./authenticationService";
import {
  getUserFbclidAttribution,
  saveUserFbclidAttribution,
} from "./userService";

export interface StoredFbclid {
  value: string;
  timestamp: number;
}

const FBCLID_STORAGE_KEY = "kinkyfantasy_fbclid";
const FBCLID_SESSION_DURATION = 90 * 24 * 60 * 60 * 1000; // 90 days
const FIRESTORE_FBCLID_CACHE_TTL_MS = 5 * 60 * 1000;
let pendingFbclidFromUrl: string | undefined;
let inFlightFirestoreFbclidPromise: Promise<string | undefined> | null = null;
let cachedFirestoreFbclid:
  | { userId: string; value: string; fetchedAt: number }
  | null = null;

function resolveFbclidUserId(explicitUserId?: string | null): string | undefined {
  const provided = explicitUserId?.trim();
  if (provided) {
    return provided;
  }
  return getCurrentUserId() || undefined;
}

function isFbclidStorageAllowed(): boolean {
  const consent = getStoredConsent();
  const region = getStoredRegionInfo();
  return isMarketingAllowed(region?.regionBucket, consent);
}

/**
 * Extract fbclid from URL search params
 */
export function extractFbclidFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get("fbclid");
  if (!fbclid) {
    return null;
  }

  try {
    return decodeURIComponent(fbclid);
  } catch {
    return fbclid;
  }
}

/**
 * Check if fbclid exists in the current URL
 */
export function hasFbclidInUrl(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has("fbclid");
}

/**
 * Capture fbclid from current URL into in-memory stash.
 * This can run before consent is available; persistence remains consent-gated.
 */
export function captureFbclidFromUrl(): string | undefined {
  if (!hasFbclidInUrl()) {
    return undefined;
  }
  const fbclid = extractFbclidFromUrl();
  if (!fbclid) {
    return undefined;
  }
  pendingFbclidFromUrl = fbclid;
  return fbclid;
}

/**
 * Save fbclid to localStorage
 */
export function saveFbclid(fbclid: string): void {
  try {
    const payload: StoredFbclid = {
      value: fbclid,
      timestamp: Date.now(),
    };
    localStorage.setItem(FBCLID_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error("Failed to save fbclid to localStorage:", error);
  }
}

/**
 * Get stored fbclid from localStorage
 * Returns null if expired or not found
 */
export function getStoredFbclid(): string | null {
  try {
    const stored = localStorage.getItem(FBCLID_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const payload = JSON.parse(stored) as StoredFbclid;
    if (
      payload.timestamp &&
      Date.now() - payload.timestamp > FBCLID_SESSION_DURATION
    ) {
      clearFbclid();
      return null;
    }

    return payload.value || null;
  } catch (error) {
    console.error("Failed to retrieve fbclid from localStorage:", error);
    return null;
  }
}

/**
 * Get the epoch-ms timestamp at which the currently stored fbclid was
 * captured (not when it's being sent now). Meta's spec for synthesizing an
 * `_fbc` value server-side from a bare fbclid requires the click's capture
 * time, so the server needs this alongside the fbclid itself.
 * Returns undefined if there is no stored fbclid or it has expired — same
 * expiry logic as getStoredFbclid().
 */
export function getStoredFbclidCapturedAt(): number | undefined {
  try {
    const stored = localStorage.getItem(FBCLID_STORAGE_KEY);
    if (!stored) {
      return undefined;
    }

    const payload = JSON.parse(stored) as StoredFbclid;
    if (
      payload.timestamp &&
      Date.now() - payload.timestamp > FBCLID_SESSION_DURATION
    ) {
      clearFbclid();
      return undefined;
    }

    return payload.timestamp || undefined;
  } catch (error) {
    console.error("Failed to retrieve fbclid capture time from localStorage:", error);
    return undefined;
  }
}

/**
 * Clear stored fbclid
 */
export function clearFbclid(): void {
  try {
    localStorage.removeItem(FBCLID_STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear fbclid from localStorage:", error);
  }
}

function promotePendingFbclid(): string | undefined {
  if (!pendingFbclidFromUrl) {
    return undefined;
  }
  const fbclid = pendingFbclidFromUrl;
  pendingFbclidFromUrl = undefined;
  saveFbclid(fbclid);
  return fbclid;
}

/**
 * Get current fbclid - either from URL or from storage
 * Priority: URL parameter > pending in-memory value > stored value
 */
export function getCurrentFbclid(): string | undefined {
  const urlFbclid = captureFbclidFromUrl();
  if (!isFbclidStorageAllowed()) {
    return undefined;
  }

  if (urlFbclid) {
    saveFbclid(urlFbclid);
    pendingFbclidFromUrl = undefined;
    return urlFbclid;
  }

  const pendingFbclid = promotePendingFbclid();
  if (pendingFbclid) {
    return pendingFbclid;
  }

  return getStoredFbclid() || undefined;
}

async function getFirestoreFbclid(userId: string): Promise<string | undefined> {
  if (cachedFirestoreFbclid && cachedFirestoreFbclid.userId !== userId) {
    cachedFirestoreFbclid = null;
  }

  const now = Date.now();
  if (
    cachedFirestoreFbclid &&
    cachedFirestoreFbclid.userId === userId &&
    now - cachedFirestoreFbclid.fetchedAt < FIRESTORE_FBCLID_CACHE_TTL_MS
  ) {
    return cachedFirestoreFbclid.value;
  }

  if (inFlightFirestoreFbclidPromise) {
    return inFlightFirestoreFbclidPromise;
  }

  inFlightFirestoreFbclidPromise = (async () => {
    try {
      const attribution = await getUserFbclidAttribution(userId);
      const fbclid = attribution?.fbclid?.trim();
      if (!fbclid) {
        return undefined;
      }
      cachedFirestoreFbclid = {
        userId,
        value: fbclid,
        fetchedAt: Date.now(),
      };
      return fbclid;
    } catch (error) {
      console.warn("Failed to read fbclid from Firestore:", error);
      return undefined;
    }
  })();

  try {
    return await inFlightFirestoreFbclidPromise;
  } finally {
    inFlightFirestoreFbclidPromise = null;
  }
}

export async function getCurrentFbclidWithFallback(
  userId?: string | null,
): Promise<string | undefined> {
  const localFbclid = getCurrentFbclid();
  if (localFbclid) {
    return localFbclid;
  }

  if (!isFbclidStorageAllowed()) {
    return undefined;
  }

  const resolvedUserId = resolveFbclidUserId(userId);
  if (!resolvedUserId) {
    return undefined;
  }

  const firestoreFbclid = await getFirestoreFbclid(resolvedUserId);
  if (firestoreFbclid) {
    saveFbclid(firestoreFbclid);
  }
  return firestoreFbclid;
}

export async function persistCurrentFbclidToFirestore(
  userId?: string | null,
): Promise<void> {
  if (!isFbclidStorageAllowed()) {
    return;
  }

  const resolvedUserId = resolveFbclidUserId(userId);
  if (!resolvedUserId) {
    return;
  }

  const fbclid = getCurrentFbclid();
  if (!fbclid) {
    return;
  }

  try {
    await saveUserFbclidAttribution(
      resolvedUserId,
      fbclid,
      "client_capture",
    );
    cachedFirestoreFbclid = {
      userId: resolvedUserId,
      value: fbclid,
      fetchedAt: Date.now(),
    };
  } catch (error) {
    console.warn("Failed to persist fbclid to Firestore:", error);
  }
}

/**
 * Initialize fbclid tracking - call this on app load
 * Returns the captured fbclid if any
 */
export function initializeFbclidTracking(): string | undefined {
  return getCurrentFbclid();
}
